import {
  Alert,
  Button,
  PageHeader,
  Skeleton,
  StatusBadge,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from '@catering/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, getRouteApi } from '@tanstack/react-router';
import { Building2, Clock, FileText, KeyRound, MapPin, UserRound, Users, Wallet } from 'lucide-react';
import type { JSX } from 'react';

import { ApiError } from '../../api/client';
import { useCurrentUser } from '../../auth/use-current-user';
import { ZONE_LABELS, getCustomer } from '../../customers/api';
import { formatTaxId } from '../../customers/format-tax-id';
import { hasPermission } from '../../auth/has-permission';
import { CustomerAccountPage } from './customer-account-page';
import { CustomerAgingPage } from './customer-aging-page';
import { CustomerContactsTab } from './components/customer-contacts-tab';
import { CustomerContractsTab } from './components/customer-contracts-tab';
import { CustomerDataTab } from './components/customer-data-tab';
import { CustomerPortalAccessTab } from './components/customer-portal-access-tab';
import { CustomerSitesTab } from './components/customer-sites-tab';

export type CustomerDetailTab =
  | 'datos'
  | 'sedes'
  | 'contactos'
  | 'contratos'
  | 'accesos'
  | 'cuenta-corriente'
  | 'antiguedad';

const customerDetailRoute = getRouteApi('/protected/customers/$customerId');

const TAB_LABELS: Record<CustomerDetailTab, string> = {
  datos: 'Datos',
  sedes: 'Sedes',
  contactos: 'Contactos',
  contratos: 'Contratos',
  accesos: 'Accesos al portal',
  'cuenta-corriente': 'Cuenta corriente',
  antiguedad: 'Antigüedad de deuda',
};

const TAB_ICONS: Record<CustomerDetailTab, typeof UserRound> = {
  datos: UserRound,
  sedes: MapPin,
  contactos: Users,
  contratos: FileText,
  accesos: KeyRound,
  'cuenta-corriente': Wallet,
  antiguedad: Clock,
};

/**
 * `customers/spec.md` → "Pantallas de alta y detalle en back-office". Tabs
 * are local state driven by the `tab` search param, not sub-routes
 * (`design.md` §7): Sedes/Contactos ship in PR 7.
 */
export function CustomerDetailPage(): JSX.Element {
  const { customerId } = customerDetailRoute.useParams();
  const { tab } = customerDetailRoute.useSearch();
  const navigate = customerDetailRoute.useNavigate();
  const currentUser = useCurrentUser();
  const canSeePortalAccess = hasPermission(currentUser.data, 'customer.portal_access.manage');
  const canSeeContracts = hasPermission(currentUser.data, 'contract.read');
  const canSeeAccountStatement = hasPermission(currentUser.data, 'payment.read');

  const query = useQuery({
    queryKey: ['customer', customerId],
    queryFn: () => getCustomer(customerId),
  });

  function goToTab(next: CustomerDetailTab): void {
    void navigate({ search: (prev) => ({ ...prev, tab: next }) });
  }

  if (query.isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (query.isError) {
    if (query.error instanceof ApiError && query.error.status === 404) {
      return (
        <Alert tone="danger" title="Empresa no encontrada">
          <Link to="/customers" className="underline">
            Volver al listado
          </Link>
        </Alert>
      );
    }

    return (
      <Alert tone="danger" title="No pudimos cargar la empresa">
        <div className="flex items-center justify-between gap-4">
          <span>Ocurrió un error al cargar. Intentá de nuevo.</span>
          <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
            Reintentar
          </Button>
        </div>
      </Alert>
    );
  }

  const customer = query.data;
  const description = `${customer.tradeName} · ${formatTaxId(customer.taxId)} · ${ZONE_LABELS[customer.zone]}`;

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        icon={Building2}
        title={customer.legalName}
        description={description}
        actions={<StatusBadge domain="customer" status={customer.status} />}
      />

      <Tabs value={tab} onValueChange={(value) => goToTab(value as CustomerDetailTab)}>
        <TabList aria-label="Pestañas de la empresa">
          {(Object.keys(TAB_LABELS) as CustomerDetailTab[])
            .filter((key) => key !== 'accesos' || canSeePortalAccess)
            .filter((key) => key !== 'contratos' || canSeeContracts)
            .filter((key) => (key !== 'cuenta-corriente' && key !== 'antiguedad') || canSeeAccountStatement)
            .map((key) => (
              <Tab key={key} value={key} icon={TAB_ICONS[key]}>
                {TAB_LABELS[key]}
              </Tab>
            ))}
        </TabList>

        <TabPanel value="datos">
          <CustomerDataTab customer={customer} onRefetch={() => void query.refetch()} />
        </TabPanel>
        <TabPanel value="sedes">
          <CustomerSitesTab customerId={customer.id} />
        </TabPanel>
        <TabPanel value="contactos">
          <CustomerContactsTab customerId={customer.id} />
        </TabPanel>
        {canSeeContracts ? (
          <TabPanel value="contratos">
            <CustomerContractsTab customerId={customer.id} />
          </TabPanel>
        ) : null}
        {canSeePortalAccess ? (
          <TabPanel value="accesos">
            <CustomerPortalAccessTab customerId={customer.id} />
          </TabPanel>
        ) : null}
        {canSeeAccountStatement ? (
          <TabPanel value="cuenta-corriente">
            <CustomerAccountPage customerId={customer.id} />
          </TabPanel>
        ) : null}
        {canSeeAccountStatement ? (
          <TabPanel value="antiguedad">
            <CustomerAgingPage customerId={customer.id} />
          </TabPanel>
        ) : null}
      </Tabs>
    </section>
  );
}
